import React from 'react';
import classes from './Invoice.module.scss';
import { Table } from '../../../components/containers/Table';
import classNames from 'classnames';
import { Invoice, InvoicePaymentMethod, InvoiceStatus } from '../../../models/graphql';
import { DateStyle, useDateString } from '../../../hooks/useDateString';
import { toTitleCase } from '../../../helpers/textHelpers';
import { Badge } from '../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../constants/badge';
import { getPaymentMethod, rateChargedToPercentage } from '../../../helpers/billingHelpers';

export type InvoiceDetailViewProps = {
  invoice: Invoice | undefined;
};

export const InvoiceDetailView = ({ invoice }: InvoiceDetailViewProps): JSX.Element => {
  const { formatDateString } = useDateString();
  const invoiceWrapperClasses = classNames('', classes.InvoiceWrapper);
  const pageHeaderClasses = classNames('grid-x', 'align-justify', classes.PageHeader);
  const invoiceHeaderClasses = classNames(
    'grid-x',
    'align-justify',
    'padding-top-small-16',
    'padding-bottom-small-16',
    'margin-bottom-small-16',
    classes.InvoiceHeader
  );
  const invoiceDetailsClasses = classNames(
    'grid-x',
    'align-justify',
    'padding-top-small-16',
    'padding-bottom-small-16',
    classes.InvoiceDetails
  );
  const invoiceFooterClasses = classNames(
    'grid-x',
    'align-justify',
    'padding-top-small-16',
    'padding-bottom-small-8',
    classes.InvoiceFooter
  );

  return (
    <div className={invoiceWrapperClasses}>
      <div className={pageHeaderClasses}>
        <h2 className={classes.SqTitle}>SmarterQueue</h2>
        <h2 className={classes.InvoiceTitle}>
          <div>Invoice</div>
          <div className={classes.InvoiceStatus}>
            {invoice?.status && (
              <Badge
                color={invoice?.status === InvoiceStatus.PAID ? BadgeColorsEnum.SELECT : BadgeColorsEnum.GREEN15}
                size={BadgeSizesEnum.MEDIUM}
                text={invoice.status}
              />
            )}
          </div>
        </h2>
      </div>
      <div className={invoiceHeaderClasses}>
        <div className={classes.InvoiceDate}>
          <label>Invoice Date:</label>
          <span>{formatDateString(invoice?.date, DateStyle.DATE_ONLY_SHORT_MONTH)}</span>
        </div>
        <div className={classes.InvoiceNumber}>
          <label>Invoice Number:</label>
          <span>{invoice?.invoiceNumber}</span>
        </div>
      </div>
      <div className={invoiceDetailsClasses}>
        <div className={`cell small-6 medium-7 ${classes.InvoiceLeftColumn}`}>
          <section>
            <label>Billed To:</label>
            <div className={classes.BillingName}>
              <div className={classes.BillingNamePersonal}>
                <span>{invoice?.billingAddressDetails.firstName}</span>
                <span>{invoice?.billingAddressDetails.lastName}</span>
              </div>
              <div className={classes.BillingEmail}>{invoice?.billingAddressDetails.email}</div>
            </div>
          </section>
          <section>
            {/* @TODO: Would it be better to have a small builder function to keep the return cleaner? */}
            {invoice?.businessAddressDetails && (
              <div className={classes.BillingAddress}>
                <div className={classes.BillingNameCompany}>{invoice?.businessAddressDetails.companyName}</div>
                <div>
                  <span>{invoice.businessAddressDetails.address1}</span>
                </div>
                <div>
                  <span>{invoice.businessAddressDetails.address2}</span>
                </div>
                <div>
                  <span>
                    {invoice.businessAddressDetails.city}, {invoice.businessAddressDetails.countryCode}
                  </span>
                </div>
                <div>
                  <span>{invoice.businessAddressDetails.postCode}</span>
                </div>
              </div>
            )}
          </section>
          <section>
            <label>Account Number</label>
            <div>{invoice?.accountNumber}</div>
          </section>
          {invoice?.vatBusinessDetails?.number && (
            <section>
              <label>Vat Number</label>
              <div>
                {invoice?.vatBusinessDetails?.numberCountryCode}
                {invoice?.vatBusinessDetails?.number}
              </div>
            </section>
          )}
        </div>
        <div className={`cell small-6 medium-5 ${classes.InvoiceRightColumn}`}>
          {invoice?.paymentMethod && (
            <section>
              <label>Payment Method:</label>
              <div>
                <div>{getPaymentMethod(invoice.paymentMethod)}</div>
                <div>
                  {invoice?.creditCard?.type}{' '}
                  {invoice.paymentMethod === InvoicePaymentMethod.CREDIT_CARD && (
                    <>**{invoice?.creditCard?.last4Digits}</>
                  )}
                </div>
                {invoice.paymentMethod === InvoicePaymentMethod.CREDIT_CARD && (
                  <div>
                    {invoice?.creditCard?.expiryMonth}/{invoice?.creditCard?.expiryYear}
                  </div>
                )}
              </div>
            </section>
          )}
          <section>
            <label>Payment Date:</label>
            <div>{formatDateString(invoice?.date, DateStyle.DATE_ONLY_SHORT_MONTH)}</div>
          </section>
          <section>
            <label>Billing Period:</label>
            <div>
              {formatDateString(invoice?.billingPeriodStartDate, DateStyle.DATE_ONLY_SHORT_MONTH)} -{' '}
              {formatDateString(invoice?.billingPeriodEndDate, DateStyle.DATE_ONLY_SHORT_MONTH)}
            </div>
          </section>
        </div>
      </div>
      <div className={classes.InvoiceTable}>
        <Table tableStyle={'basic'}>
          <>
            <thead>
              <tr>
                <th>Item</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice?.renewalType && invoice.planType && (
                <tr>
                  <td>
                    Plan: {toTitleCase(invoice?.planType)} {toTitleCase(invoice?.renewalType)}
                  </td>
                  <td>
                    {invoice.planAmount} {invoice.currencyCode}
                  </td>
                </tr>
              )}
              {invoice?.addonsAmount && invoice.addonsAmount !== '0.00' && (
                <tr>
                  <td>Add-Ons</td>
                  <td>
                    {invoice.addonsAmount} {invoice.currencyCode}
                  </td>
                </tr>
              )}
              {invoice?.discountAmount && invoice.discountAmount !== '0.00' && (
                <tr>
                  <td>Discount</td>
                  <td>
                    {invoice.discountAmount} {invoice.currencyCode}
                  </td>
                </tr>
              )}
              {invoice?.preVatAmount && (
                <tr className={classes.TotalAmountSinVatRow}>
                  <td>Total (excluding VAT)</td>
                  <td>
                    {invoice.preVatAmount} {invoice.currencyCode}
                  </td>
                </tr>
              )}
              {invoice?.vatBusinessDetails && invoice.vatAmount && (
                <tr>
                  <td>
                    {rateChargedToPercentage(invoice.vatBusinessDetails.rateCharged)} VAT (
                    {invoice.vatBusinessDetails.countryChargedDisplayName})
                  </td>
                  <td>
                    {invoice.vatAmount} {invoice.currencyCode}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td className={classes.FooterLeftColumn}>
                  <h5>Total</h5>
                </td>
                {invoice?.amount && (
                  <td className={classes.FooterRightColumn}>
                    <h5>
                      {invoice.amount} {invoice.currencyCode}
                    </h5>
                  </td>
                )}
              </tr>
            </tfoot>
          </>
        </Table>
        {invoice?.vatReverseCharge && (
          <div className={'margin-top-small-24 margin-bottom-small-24'}>Reverse-charge VAT</div>
        )}
        <div className={classes.FooterWrapper}>
          <div className={invoiceFooterClasses}>
            <div className={`cell small-6 medium-7 {$classes.FooterLeftColumn}`}>
              <div>SmarterQueue is a limited Company registered in the UK</div>
              <div>(Company #09981623, VAT number GB 298326460)</div>
              <div>
                <a href={'mailto://smarterqueue.com'}>smarterqueue.com</a>
              </div>
            </div>
            <div className={`cell small-6 medium-5 ${classes.FooterRightColumn}`}>
              <div>27 Old Gloucester Street</div>
              <div>WC1 3AX</div>
              <div>London, UK</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
